import { FC, useEffect, useState } from "react";
import { DocumentGridStateType, ISaveFocus } from "../../../../@types/documents";
import { DocumentStatusType, DocumentType, IdTableVariant, LoadingStatus, SysOptionName } from "../../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../../components/plugins";
import { Spinner } from "../../../../components/spiner/Spinner";
import { TabsPanel } from "../../../../components/tabs";
import useGridFilter, { DefaultDateGridFilter, MakeGridFilter } from "../../../../system/hooks/useGridFilter";
import { useDetailsTabsPanel } from "../../../../system/hooks/useTabsPanel";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../../system/providers/plugin";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import styles from "../../../styles/index.module.scss";
import tabsStyles from "../../../../pages/styles/homePage.module.scss";
import classNames from "classnames";
import { UserActionLogModal } from "../../../Service/UserActionLog/view/UserActionLogModal";
import { InventorySvedDataProvider } from "../../../../Services/DataProviders/InventorySvedDataProvider";
import { IInventorySvedViewDTO } from "../../../../libs/coreapi-dto/documents/invoice/inventory/inventorySved";
import { useTranslation } from "react-i18next";
import DefaultDocumentsCommandsPanelV3 from "../../../../components/commandsPanels/DefaultDocumentsCommandsPanelV3";
import { ConfirmationItemsModal } from "../../../../components/modalWindows/ConfirmationItemsModal";
import { AllowDTO } from "../../../../libs/coreapi-dto/dirs/allow";
import { ProgressActionSpinner } from "../../../../components/progressActionSpinner/ProgressActionSpinner";
import { SysOptionsDataProvider } from "../../../../Services/DataProviders/SysOptionsDataProvider";
import { ISysOptionsDTO } from "../../../../libs/coreapi-dto/service/sysOptions";
import { promisifyMethodWithFallback } from "../../../../system/functions/promisifyMethod";
import { IInventorySvedItemViewDTO } from "../../../../libs/coreapi-dto/documents/invoice/inventory/inventorySvedItem";
import { Kiz2InventoryVedDataProvider } from "../../../../Services/DataProviders/Kiz2InventoryVedDataProvider";
import { IKiz2InventoryVedAllowDTO } from "../../../../libs/coreapi-dto/dirs/kiz";



export const InventorySvedPluginView: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const pluginCtx = usePluginContext();
    const tabCtx = useTabsContext();
    const inventorySvedDP = new InventorySvedDataProvider(appContext.coreApiService);
    const kiz2InventoryVedDP = new Kiz2InventoryVedDataProvider(appContext.coreApiService);

    const [viewState, setViewState] = useState<DocumentGridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultDateGridFilter());
    const [data, setData] = useState<IInventorySvedViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.Completed);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [showDetailsTabs, setShowDetailsTabs] = useState<boolean>(false);
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    const [documentStatusType, setDocumentStatusType] = useState<DocumentStatusType | undefined>();
    const [selectedDocumentState, setSelectedDocumentState] = useState<DocumentStatusType | undefined>();
    const [multipleSelect, setMultipleSelect] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItemView, setSelectedItemViews] = useState<IInventorySvedViewDTO>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);

    // Modal Log
    const [showModalLog, setShowModalLog] = useState<boolean>(false);

    // Локализация
    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.inventorySved." + value);
    const baseVedT = (value: string) => t("documents.inventoryVed." + value);

    const [saveFocus, setSaveFocus] = useState<ISaveFocus>({
        viewState: 'view',
        selectedItem: selectedItem,
        data: data
    });

    const sysOptionsDataProvider = new SysOptionsDataProvider(appContext.coreApiService);
    const [canCreate552Document, setCanCreate552Document] = useState<boolean>(false);

    useEffect(() => {
        sysOptionsDataProvider.getByName(SysOptionName.InventoryVedEnableAutoSendKiz, (data: ISysOptionsDTO | undefined) => {
            if (data) {
                const result = JSON.parse(data.jsonData).Value;
                setCanCreate552Document(!result)
            }
        })
    }, [])

    useEffect(() => {
        if (viewState === "view") {
            setIsSubmitting(true);
            inventorySvedDP.getView(gridFilter, async (data, totalCount) => {
                setData(data);
                setTotalCount(totalCount);
                setIsSubmitting(false);
                setLoadingStatus(totalCount > 0 ? LoadingStatus.Completed : LoadingStatus.NoData);
            });
        }

        if (viewState === "refresh") {
            setViewState("view");
        }
    }, [gridFilter, viewState]);

    useEffect(() => {
        if (tabCtx.currentTab?.title === `${baseT("tabName")}`) {
            setShowDetailsTabs(true);
        } else {
            setShowDetailsTabs(false);
        }
        pluginCtx.masterGrid.onUnselectEvent();
        setShowDetailsTabs(true);
    }, [tabCtx.currentTab]);

    useEffect(() => {
        if (!multipleSelect) {
            setSelectedItems([]);
        } else {
            setSelectedItem(undefined);
        }

        if (selectedItem) {
            const documentState = selectedItem.cells.find((c) => c.propertyName === "documentState")?.value as DocumentStatusType;
            setSelectedDocumentState(documentState ?? undefined);
            setSelectedItemViews(data.find((x) => x.idGlobal === selectedItem.idGlobal));
        } else {
            setSelectedItemViews(undefined);
        }
    }, [multipleSelect, selectedItem]);

    useEffect(() => {
        pluginCtx.masterGrid.onUnselectEvent();
    }, [documentStatusType, gridFilter, viewState]);

    const deleteRow = () => {
        const isAlreadyDeleted = selectedItem?.[0]?.isDeleted;
        const isDocumentLocked = selectedDocumentState === "save" || selectedDocumentState === "proc";

        if (isAlreadyDeleted || isDocumentLocked) {
            return;
        }

        inventorySvedDP.markDelete(selectedItem?.idGlobal as string, () => {
            setViewState("refresh");
        });
    };

    const controlKiz = async (idDocumentGlobal: string): Promise<boolean> => {

        // Контроль КИЗ по каждой ведомости
        renderProgressView("Проверка коррекности вложенных документов инвентаризации");
        try {
            const svedItems = await promisifyMethodWithFallback<IInventorySvedItemViewDTO[], typeof inventorySvedDP>(
                inventorySvedDP,
                inventorySvedDP.getItemsView,
                idDocumentGlobal,
                MakeGridFilter("dateCreated")
            )

            // Итерируем валидацию
            if (svedItems.length > 0) {
                console.log(svedItems)
                for (const svedItem of svedItems) {
                    renderProgressView(`Проверка ведомости ${svedItem.inventoryVedName}`);
                    try {
                        const allowResult = await promisifyMethodWithFallback<IKiz2InventoryVedAllowDTO, typeof kiz2InventoryVedDP>(
                            kiz2InventoryVedDP,
                            kiz2InventoryVedDP.allowDocumentItems,
                            { idDocument: svedItem.idInventoryVedGlobal }
                        );

                        if (!allowResult.isAllowed && allowResult.items?.length > 0) {
                            setModalWindow(<></>)
                            renderConfirmationModal(
                                baseVedT("messages.validationProcessName") + ` ${svedItem.inventoryVedName}`,
                                allowResult.title,
                                allowResult.items.join('\r\n'),
                                allowResult.details,
                                () => {
                                    setConfirmationItemsModalJsx(<></>)
                                }
                            );

                            return false;
                        }
                    }
                    catch (err) {
                        setModalWindow(<></>)
                        setConfirmationItemsModalJsx(<></>)

                        return false;
                    }
                }

                setModalWindow(<></>)
                setConfirmationItemsModalJsx(<></>)

                return true;
            }
            else {
                setModalWindow(<></>)
                setConfirmationItemsModalJsx(<></>)

                return false;
            }

        }
        catch (err) {
            console.error(err);

            setModalWindow(<></>)
            setConfirmationItemsModalJsx(<></>)

            return false;
        }
    }

    const controlLotMovements = async (idDocumentGlobal: string, sendData: () => void) => {

        // Проверка товарных движений        
        renderProgressView("Проверка товарных движений");
        try {
            const allowLotMovementsResult = await promisifyMethodWithFallback<AllowDTO, typeof inventorySvedDP>(
                inventorySvedDP,
                inventorySvedDP.checkLotMovements,
                idDocumentGlobal
            );

            setModalWindow(<></>)
            if (!allowLotMovementsResult.isAllowed) {
                renderConfirmationModal(
                    allowLotMovementsResult.title,
                    allowLotMovementsResult.details,
                    allowLotMovementsResult.items.join('\r\n'),
                    allowLotMovementsResult.question,
                    () => setConfirmationItemsModalJsx(<></>),
                    () => {
                        sendData()
                        setConfirmationItemsModalJsx(<></>)
                    }
                )
            } else {
                sendData()
            }
        }
        catch (err) {
            console.error(err);

            setModalWindow(<></>)
            setConfirmationItemsModalJsx(<></>)
        }
    }

    const processPreliminaryHandleWithConfirmation = async (idDocumentGlobal: string, sendData: () => void) => {

        // Контроль КИЗ
        const controlKizResult: boolean = await controlKiz(idDocumentGlobal);
        if (!controlKizResult)
            return;
        
        await controlLotMovements(idDocumentGlobal, sendData);
    }

    const [сonfirmationItemsModalJsx, setConfirmationItemsModalJsx] = useState<JSX.Element>(<></>);
    const renderConfirmationModal = (header: string, warning: string, items: string, question, cancel: () => void, ok?: () => void) => {
        setConfirmationItemsModalJsx(
            <ConfirmationItemsModal
                header={`${header}`}
                warning={`${warning}`}
                listPositions={`${items}`}
                question={`${question}`}
                cancel={cancel}
                largeTextArea
                ok={ok}
            />
        );
    }

    const [modalWindow, setModalWindow] = useState(<></>);
    const renderProgressView = (actionMessage: string) => {
        setModalWindow(
            <ProgressActionSpinner
                loaderText={`${actionMessage}`}
            />
        );
    };

    const create552Document = () => {
        const isDocumentLocked = selectedDocumentState !== "proc";
        if (!selectedItem || isDocumentLocked || !canCreate552Document) return;

        const idDocumentGlobal: string = selectedItem?.idGlobal as string;
        inventorySvedDP.create552Document(idDocumentGlobal, (result: boolean | null) => {
            if (!result) {
                console.error("Ошибка повторной отправки документа 552");
            }
        })
    }

    return (
        <>
            <PluginWrapper>
                <ToolbarWrapper>
                    <DefaultDocumentsCommandsPanelV3
                        selectedItems={[selectedItems,setSelectedItems]}
                        selectedItem={[selectedItem, setSelectedItem]}
                        saveFocus={[saveFocus, setSaveFocus]}
                        gridFilter={[gridFilter, dispatchGridFilter]}
                        plugin="inventory_sved_create_plugin"
                        documentStatusType={[documentStatusType, setDocumentStatusType]}
                        selectedDocumentStatusType={[selectedDocumentState, setSelectedDocumentState]}
                        multipleSelect={[multipleSelect, setMultipleSelect]}
                        dataProvider={inventorySvedDP}
                        pluginSettings={props.plugin}
                        idTable={IdTableVariant.InventorySved}
                        setViewState={(vs) => setViewState(vs)}
                        advancedFeatures
                        items={
                            [
                                {
                                    label: baseT("create522Document"),
                                    onClick: () => selectedItem && create552Document(),
                                    disabled: !selectedItem || !canCreate552Document || selectedDocumentState !== "proc"
                                },
                                {
                                    label: t(`${t("general.showUserLog")}`),
                                    onClick: () => selectedItem && setShowModalLog(true),
                                    disabled: !selectedItem,
                                },
                            ]
                        }
                        mnemocode={selectedItemView?.mnemocode}
                        processPreliminaryHandleWithConfirmation={processPreliminaryHandleWithConfirmation}
                    />
                </ToolbarWrapper>
                <div>
                    <div>
                        {isSubmitting ? (
                            <Spinner />
                        ) : (
                            <DefaultGrid
                                openWithEnterForEdit={{
                                    pluginTabContext: "inventory_sved_create_plugin",
                                    mnemocode: selectedItemView?.mnemocode,
                                    selectedDocumentState
                                }}
                                gridId={props.gridId}
                                data={data}
                                documentStatus
                                separator
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                actionGridRow={{ delete: () => deleteRow() }}
                                filter={gridFilter}
                                hiddenPagination={undefined}
                                dataProvider={inventorySvedDP}
                                totalCount={totalCount}
                                plugin={props.plugin}
                                multipleSelect={multipleSelect}
                                selectedItem={selectedItem}
                                selectedItems={selectedItems}
                                saveFocus={[saveFocus, setSaveFocus]}
                                onSelect={(row) => {
                                    setSelectedItem(row);
                                    row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.actDeduction) : pluginCtx.masterGrid.onUnselectEvent();
                                }}
                                onMultipleSelect={(rows) => setSelectedItems(rows)}
                                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                                onFilterDelete={(i) => {
                                    i.propertyName === "documentState" && setDocumentStatusType(undefined);
                                    dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName });
                                }}
                                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            />
                        )}
                    </div>
                    <div className={styles.detailsTabView}>
                        {showDetailsTabs && detailsTabsPanel.tabs.length > 0 && selectedItem && !multipleSelect && (
                            <>
                                <div className={tabsStyles.tabsPanelWrapper}>
                                    <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                        <TabsPanel
                                            id="detailsTabsPanel"
                                            activeId={detailsTabsPanel.currentTab?.id}
                                            tabs={detailsTabsPanel.tabs}
                                            onActive={(id) => dispatchDetailsTabsPanel({ type: "activate", payload: id })}
                                        ></TabsPanel>
                                    </div>
                                </div>
                                <div className={tabsStyles.contentWrapper}>
                                    {detailsTabsPanel.tabs.map((item) => {
                                        return (
                                            <div key={item.id}
                                                className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                {item.view.content}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                    {showModalLog && <UserActionLogModal idDocumentGlobal={selectedItem?.idGlobal} onClick={() => setShowModalLog(false)} />}
                    {modalWindow}
                    {сonfirmationItemsModalJsx}
                </div>
            </PluginWrapper>
        </>
    );
};